import { useCallback, useMemo, useState } from 'react'
import type { components } from '../../api/generated/schema'
import type { Notice } from '../../lib/envelope'
import { describeApiFailure } from '../../lib/failure'
import { caseApi, useCaseMutation } from './mutations'

type Schema = components['schemas']

/**
 * Every case mutation, in one place — the same bundling as characters and campaigns, so
 * the page renders notices and failures once rather than once per card.
 *
 * `canEdit` is the server's `isGameMaster`: members read a case, the Game Master writes it.
 */
export function useCaseEditing(caseId: number, canEdit: boolean) {
  const [notices, setNotices] = useState<Notice[]>([])
  const [failure, setFailure] = useState<string | null>(null)

  const onEnvelope = useCallback((envelope: { notices: Notice[] }) => {
    setNotices(envelope.notices)
    setFailure(null)
  }, [])

  const onFailure = useCallback((error: unknown) => {
    setNotices([])
    setFailure(describeApiFailure(error, 'Only the Game Master can change this case.'))
  }, [])

  const base = useMemo(() => ({ caseId, onEnvelope, onFailure }), [caseId, onEnvelope, onFailure])

  const update = useCaseMutation<Schema['CaseRequest']>({
    ...base,
    request: (body) => caseApi.update(caseId, body),
  })

  const addCharacter = useCaseMutation<{ characterId: number }>({
    ...base,
    request: ({ characterId }) => caseApi.addCharacter(caseId, characterId),
  })

  const removeCharacter = useCaseMutation<{ characterId: number }>({
    ...base,
    request: ({ characterId }) => caseApi.removeCharacter(caseId, characterId),
  })

  // Creating a scene is not in this bundle: it returns a scene sheet rather than a case
  // envelope, so it invalidates instead of patching. See `useCreateScene` in queries.ts.
  const deleteScene = useCaseMutation<{ sceneId: number }>({
    ...base,
    request: ({ sceneId }) => caseApi.deleteScene(caseId, sceneId),
  })

  const reorderScenes = useCaseMutation<{ ids: number[] }>({
    ...base,
    request: ({ ids }) => caseApi.reorderScenes(caseId, ids),
  })

  const all = [update, addCharacter, removeCharacter, deleteScene, reorderScenes]

  return {
    canEdit,
    notices,
    failure,
    dismiss: useCallback(() => {
      setNotices([])
      setFailure(null)
    }, []),
    isPending: all.some((mutation) => mutation.isPending),
    update,
    addCharacter,
    removeCharacter,
    deleteScene,
    reorderScenes,
  }
}

export type CaseEditing = ReturnType<typeof useCaseEditing>
